import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { portalApi, tokens, login as apiLogin } from './api';
import { AccessLevel, PermissionGrid, satisfies } from './permissions';

export interface CurrentUser {
  id: string;
  username: string;
  email: string | null;
  fullName: string;
  role: { key: string; name: string; isSuperadmin: boolean } | null;
  isSuperadmin: boolean;
  permissions: PermissionGrid;
}

interface AuthState {
  user: CurrentUser | null;
  loading: boolean;
  signIn: (u: string, p: string) => Promise<void>;
  signOut: () => void;
  /** Superadmin passes everything, so new modules never lock the owners out. */
  can: (module: string, level?: AccessLevel) => boolean;
}

export const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [loading, setLoading] = useState(true);

  async function loadMe() {
    if (!tokens.access) { setUser(null); setLoading(false); return; }
    try {
      setUser(await portalApi.get<CurrentUser>('/auth/me'));
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void loadMe(); }, []);

  const value: AuthState = {
    user,
    loading,
    async signIn(username, password) {
      await apiLogin(username, password);
      setLoading(true);
      await loadMe();
    },
    signOut() {
      tokens.clear();
      setUser(null);
    },
    can(module, level = 'read') {
      if (!user) return false;
      if (user.isSuperadmin) return true;
      return satisfies(user.permissions, module, level);
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
